/*eslint-disable no-unused-vars */
/*eslint-disable react-hooks/exhaustive-deps*/
import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Button, Col, Input, Modal, ModalBody, ModalFooter, ModalHeader, Row, Table, Badge, Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';

import { useNavigate } from 'react-router-dom';
import { setLoadingOverlay } from '../../store/slices/loadingOverlaySlice';
import LocalstorageService from '../../helpers/localstorage-service';
import { CompanyId } from '../../constants';
import profitabilityService from '../../services/profitability';
import NoRecords from '../../components/Table/NoRecords';

const MONTHS_HE = ['', 'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
  'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];

const fmt = (v) => (v == null ? '—' : Number(v).toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

const InvoiceTracking = () => {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const { t } = useTranslation();
  const [setPageTitle, setCrumbItem] = useOutletContext();

  const companyId = LocalstorageService.getItem(CompanyId);
  const now = new Date();

  // חשבוניות מתקבלות בגין החודש הקודם — ברירת מחדל = חודש קודם
  const prevMonth = now.getMonth() === 0 ? 12 : now.getMonth();
  const prevYear  = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
  const [month, setMonth]         = useState(prevMonth);
  const [year, setYear]           = useState(prevYear);
  const [activeTab, setActiveTab] = useState('all');
  const [list, setList]           = useState([]);
  const [missingList, setMissingList] = useState([]);
  const [groupByEmployee, setGroupByEmployee] = useState(false);
  const [deleteInvoiceId,  setDeleteInvoiceId]  = useState(null);
  const [sortEmployee,     setSortEmployee]     = useState(null); // null | 'asc' | 'desc'

  // ── תצוגה מקדימה ──────────────────────────────────────────
  const [previewUrl,      setPreviewUrl]      = useState(null);
  const [previewFileName, setPreviewFileName] = useState('');
  const [previewLoading,  setPreviewLoading]  = useState(false);

  useEffect(() => {
    setPageTitle(t('PROFITABILITY.INVOICES_PAGE.PAGE_TITLE'));
    setCrumbItem([
      { title: t('PROFITABILITY.INVOICES_PAGE.CRUMB_PARENT'), link: '#' },
      { title: t('PROFITABILITY.INVOICES_PAGE.CRUMB_ITEM'), link: '#' },
    ]);
  }, []);

  const fetchInvoices = async () => {
    dispatch(setLoadingOverlay(true));
    try {
      const [allRes, scheduleRes] = await Promise.all([
        profitabilityService.getInvoices({ companyId, month, year, missingOnly: false }),
        profitabilityService.getPaymentSchedule({ companyId, month, year }),
      ]);
      const allInvoices = allRes.data?.dataList || [];
      const schedule    = scheduleRes.data?.dataList || [];

      setList(allInvoices);

      // מסופים שיש להם חשבונית שהתקבלה
      const receivedSet = new Set(
        allInvoices.filter(r => r.received).map(r => r.terminalNumber)
      );

      // מיפוי מסוף → שורת לוח תשלומים (לצורך הסכום הצפוי ושם העובד)
      const scheduleMap = schedule.reduce((acc, s) => {
        acc[s.terminalNumber] = s;
        return acc;
      }, {});

      // מסופים מלוח התשלומים שאין להם חשבונית שהתקבלה
      const fromSchedule = schedule
        // דלג על מסופים עם סכום צפוי 0 (לא פעילים / ללא תשלום בחודש זה)
        .filter(s => ((s.fixPayment || 0) + (s.estimatedPerBeat || 0)) > 0)
        .filter(s => !receivedSet.has(s.terminalNumber))
        .map(s => {
          const existing = allInvoices.find(r => r.terminalNumber === s.terminalNumber);
          const expected  = (s.fixPayment || 0) + (s.estimatedPerBeat || 0);
          const empName   = s.employeeName || '';
          if (existing) {
            return { ...existing, expectedAmount: expected, employeeName: empName };
          }
          return {
            id: null,
            terminalNumber:  s.terminalNumber,
            businessName:    s.locationName,
            received:        false,
            amountBeforeVat: null,
            amountIncVat:    null,
            expectedAmount:  expected,
            employeeName:    empName,
            blobPath:        null,
            fromScheduleOnly: true,
          };
        });

      setMissingList(fromSchedule);
    } catch {
      toast.error(t('PROFITABILITY.INVOICES_PAGE.TOAST_ERROR_LOAD'));
    } finally {
      dispatch(setLoadingOverlay(false));
    }
  };

  useEffect(() => { fetchInvoices(); }, [month, year]);

  const handleToggle = async (id) => {
    // מסופים ללא רשומה ב-DB — לא ניתן לשנות סטטוס
    if (!id) {
      toast.info('לא ניתן לסמן — טרם שויכה חשבונית למסוף זה. עבור לשיוך חשבוניות סרוקות.');
      return;
    }
    try {
      await profitabilityService.saveInvoice({ action: 'TOGGLE', id, companyId });
      fetchInvoices();
    } catch {
      toast.error(t('PROFITABILITY.INVOICES_PAGE.TOAST_ERROR_TOGGLE'));
    }
  };

  const handleDeleteInvoice = (id) => setDeleteInvoiceId(id);

  const confirmDeleteInvoice = async () => {
    const id = deleteInvoiceId;
    setDeleteInvoiceId(null);
    try {
      await profitabilityService.saveInvoice({ action: 'DELETE', id, companyId });
      toast.success('החשבונית נמחקה בהצלחה');
      fetchInvoices();
    } catch {
      toast.error('שגיאה במחיקת החשבונית');
    }
  };

  const openInvoiceFile = async (row) => {
    setPreviewFileName(row.originalFileName || row.businessName || '');
    setPreviewLoading(true);
    setPreviewUrl('loading');
    try {
      const res = await profitabilityService.getInvoiceFileUrl({ id: row.id, companyId });
      const url = res.data?.data?.url;
      if (url) setPreviewUrl(url);
      else {
        toast.error(t('PROFITABILITY.INVOICES_PAGE.TOAST_NO_FILE'));
        setPreviewUrl(null);
      }
    } catch {
      toast.error(t('PROFITABILITY.INVOICES_PAGE.TOAST_ERROR_FILE'));
      setPreviewUrl(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  // ── אקסל — כל החשבוניות ──────────────────────────────────
  const exportExcel = (rows, filename) => {
    const data = rows.map(r => ({
      מסוף: r.terminalNumber,
      'שם עסק': r.businessName,
      'לפני מע"מ': r.amountBeforeVat,
      'כולל מע"מ': r.amountIncVat,
      'תאריך חשבונית': r.invoiceDate || '',
      'תאריך סריקה': r.scanDate || '',
      'שם על החשבונית': r.invoiceName || '',
      שייך: r.belongsTo || '',
      'מספר חשבונית': r.invoiceNumber || '',
      הערות: r.notes || '',
      התקבל: r.received ? 'כן' : 'לא',
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'חשבוניות');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf]), filename);
  };

  // ── אקסל — חשבוניות חסרות ────────────────────────────────
  const exportMissingExcel = (rows, filename) => {
    const data = rows.map(r => ({
      מסוף:            r.terminalNumber,
      'שם עסק':        r.businessName,
      עובד:            r.employeeName || '',
      'צפוי לפני מע"מ': r.expectedAmount ? Number((r.expectedAmount / 1.18).toFixed(2)) : '',
      'צפוי כולל מע"מ':  r.expectedAmount ? Number(r.expectedAmount.toFixed(2)) : '',
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'חסרות');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf]), filename);
  };

  const yearOptions = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 5; y--) yearOptions.push(y);

  // ── טבלת "כל החשבוניות" ─────────────────────────────────
  const renderTable = (rows, showExport) => (
    rows.length === 0 ? <NoRecords /> : (
      <>
        {showExport && (
          <div className="mb-2 d-flex gap-2 justify-content-end">
            <Button color="primary" outline size="sm"
              onClick={() => navigate('/profitability/invoice-scan')}>
              <i className="ri-scan-2-line me-1"></i>{t('PROFITABILITY.INVOICES_PAGE.ASSIGN_SCANNED')}
            </Button>
            <Button color="success" outline size="sm"
              onClick={() => exportExcel(rows, `חשבוניות-${month}-${year}.xlsx`)}>
              <i className="ri-file-excel-line me-1"></i>{t('PROFITABILITY.INVOICES_PAGE.EXPORT_EXCEL')}
            </Button>
          </div>
        )}
        <div className="table-responsive">
          <Table bordered hover size="sm" className="text-nowrap">
            <thead className="table-dark">
              <tr>
                <th className="text-center" style={{ width: 48 }}>{t('PROFITABILITY.INVOICES_PAGE.TABLE.RECEIVED')}</th>
                <th>{t('PROFITABILITY.INVOICES_PAGE.TABLE.TERMINAL')}</th>
                <th>{t('PROFITABILITY.INVOICES_PAGE.TABLE.BUSINESS_NAME')}</th>
                <th className="text-end">{t('PROFITABILITY.INVOICES_PAGE.TABLE.BEFORE_VAT')}</th>
                <th className="text-end">{t('PROFITABILITY.INVOICES_PAGE.TABLE.INC_VAT')}</th>
                <th>{t('PROFITABILITY.INVOICES_PAGE.TABLE.INVOICE_DATE')}</th>
                <th>{t('PROFITABILITY.INVOICES_PAGE.TABLE.INVOICE_NAME')}</th>
                <th>{t('PROFITABILITY.INVOICES_PAGE.TABLE.BELONGS_TO')}</th>
                <th>{t('PROFITABILITY.INVOICES_PAGE.TABLE.FILE')}</th>
                <th>{t('PROFITABILITY.INVOICES_PAGE.TABLE.NOTES')}</th>
                <th style={{ width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id ?? r.terminalNumber} className={r.received ? '' : 'table-warning'}>
                  <td className="text-center">
                    <button
                      className="btn btn-sm p-0 border-0 bg-transparent"
                      onClick={() => handleToggle(r.id)}
                      title={r.received ? t('PROFITABILITY.INVOICES_PAGE.TOGGLE_NOT_RECEIVED') : t('PROFITABILITY.INVOICES_PAGE.TOGGLE_RECEIVED')}
                    >
                      {r.received
                        ? <i className="ri-checkbox-circle-fill text-success fs-5"></i>
                        : <i className="ri-close-circle-line text-danger fs-5"></i>}
                    </button>
                  </td>
                  <td>{r.terminalNumber}</td>
                  <td>{r.businessName}</td>
                  <td className="text-end">{fmt(r.amountBeforeVat)}</td>
                  <td className="text-end">{fmt(r.amountIncVat)}</td>
                  <td className="text-muted small">{r.invoiceDate || '—'}</td>
                  <td>{r.invoiceName || ''}</td>
                  <td>
                    {r.belongsTo
                      ? <Badge color="secondary" className="small">{r.belongsTo}</Badge>
                      : ''}
                  </td>
                  <td>
                    {r.blobPath ? (
                      <button
                        className="btn btn-sm p-0 border-0 bg-transparent"
                        onClick={() => openInvoiceFile(r)}
                        title={t('PROFITABILITY.INVOICES_PAGE.OPEN_FILE')}>
                        <i className="ri-eye-line text-primary fs-5"></i>
                      </button>
                    ) : ''}
                  </td>
                  <td className="text-muted small">{r.notes || ''}</td>
                  <td className="text-center">
                    <button
                      className="btn btn-sm p-0 border-0 bg-transparent"
                      onClick={() => handleDeleteInvoice(r.id)}
                      title="מחק חשבונית">
                      <i className="ri-delete-bin-line text-danger"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="table-secondary fw-bold">
                <td colSpan={3}>{t('PROFITABILITY.INVOICES_PAGE.FOOTER.TOTAL')}</td>
                <td className="text-end">
                  {fmt(rows.reduce((s, r) => s + (r.amountBeforeVat || 0), 0))}
                </td>
                <td className="text-end">
                  {fmt(rows.reduce((s, r) => s + (r.amountIncVat || 0), 0))}
                </td>
                <td colSpan={6}></td>
              </tr>
            </tfoot>
          </Table>
        </div>
      </>
    )
  );

  // ── שורת טבלה — חסרות ────────────────────────────────────
  const MissingRow = ({ r }) => (
    <tr key={r.id ?? r.terminalNumber}>
      <td className="text-center">
        {r.id
          ? (
            <button
              className="btn btn-sm p-0 border-0 bg-transparent"
              onClick={() => handleToggle(r.id)}
              title={t('PROFITABILITY.INVOICES_PAGE.TOGGLE_RECEIVED')}>
              <i className="ri-close-circle-line text-danger fs-5"></i>
            </button>
          )
          : (
            <i className="ri-subtract-line text-muted fs-5"
              title="אין רשומה — עבור לשיוך חשבוניות סרוקות"></i>
          )
        }
      </td>
      <td className="fw-semibold">{r.terminalNumber}</td>
      <td>{r.businessName}</td>
      <td className="text-end text-muted">
        {r.expectedAmount ? fmt(r.expectedAmount / 1.18) : '—'}
      </td>
      <td className="text-end text-muted">
        {r.expectedAmount ? fmt(r.expectedAmount) : '—'}
      </td>
      <td>
        {r.employeeName
          ? <Badge color="info" className="small text-dark">{r.employeeName}</Badge>
          : ''}
      </td>
    </tr>
  );

  // ── טבלת "חסרות" — עם/בלי קיבוץ לפי עובד ──────────────
  const renderMissingTable = (rows) => {
    if (rows.length === 0) return <NoRecords />;

    const toolbar = (
      <div className="mb-2 d-flex gap-2 justify-content-between align-items-center flex-wrap">
        <div className="d-flex gap-2">
          <Button
            color={groupByEmployee ? 'primary' : 'outline-primary'}
            size="sm"
            onClick={() => setGroupByEmployee(g => !g)}>
            <i className="ri-group-line me-1"></i>
            {groupByEmployee ? 'בטל קיבוץ לפי עובד' : 'קבץ לפי עובד'}
          </Button>
        </div>
        <div className="d-flex gap-2">
          <Button color="primary" outline size="sm"
            onClick={() => navigate('/profitability/invoice-scan')}>
            <i className="ri-scan-2-line me-1"></i>{t('PROFITABILITY.INVOICES_PAGE.ASSIGN_SCANNED')}
          </Button>
          <Button color="success" outline size="sm"
            onClick={() => exportMissingExcel(rows, `חשבוניות-חסרות-${month}-${year}.xlsx`)}>
            <i className="ri-file-excel-line me-1"></i>{t('PROFITABILITY.INVOICES_PAGE.EXPORT_EXCEL')}
          </Button>
        </div>
      </div>
    );

    const thead = (
      <thead className="table-dark">
        <tr>
          <th className="text-center" style={{ width: 48 }}>V</th>
          <th>{t('PROFITABILITY.INVOICES_PAGE.TABLE.TERMINAL')}</th>
          <th>{t('PROFITABILITY.INVOICES_PAGE.TABLE.BUSINESS_NAME')}</th>
          <th className="text-end">לפני מע"מ (צפוי)</th>
          <th className="text-end">כולל מע"מ (צפוי)</th>
          <th style={{ cursor: 'pointer', userSelect: 'none' }}
            onClick={() => setSortEmployee(s => s === 'asc' ? 'desc' : 'asc')}>
            עובד{' '}
            {sortEmployee === 'asc' ? '↑' : sortEmployee === 'desc' ? '↓' : '↕'}
          </th>
        </tr>
      </thead>
    );

    const totalExpected = rows.reduce((s, r) => s + (r.expectedAmount || 0), 0);

    const tfoot = (
      <tfoot>
        <tr className="table-secondary fw-bold">
          <td colSpan={3}>סה"כ ({rows.length} מסופים)</td>
          <td className="text-end">{fmt(totalExpected / 1.18)}</td>
          <td className="text-end">{fmt(totalExpected)}</td>
          <td></td>
        </tr>
      </tfoot>
    );

    const sortedRows = sortEmployee
      ? [...rows].sort((a, b) => {
          const ea = (a.employeeName || '').toLowerCase();
          const eb = (b.employeeName || '').toLowerCase();
          return sortEmployee === 'asc' ? ea.localeCompare(eb, 'he') : eb.localeCompare(ea, 'he');
        })
      : rows;

    if (!groupByEmployee) {
      return (
        <>
          {toolbar}
          <div className="table-responsive">
            <Table bordered hover size="sm" className="text-nowrap">
              {thead}
              <tbody>
                {sortedRows.map(r => <MissingRow key={r.id ?? r.terminalNumber} r={r} />)}
              </tbody>
              {tfoot}
            </Table>
          </div>
        </>
      );
    }

    // ── קיבוץ לפי עובד ───────────────────────────────────
    const groups = {};
    sortedRows.forEach(r => {
      const emp = r.employeeName || '— לא משויך —';
      if (!groups[emp]) groups[emp] = [];
      groups[emp].push(r);
    });
    const sortedGroups = Object.entries(groups).sort(([a], [b]) => a.localeCompare(b, 'he'));

    return (
      <>
        {toolbar}
        {sortedGroups.map(([empName, empRows]) => {
          const empTotal = empRows.reduce((s, r) => s + (r.expectedAmount || 0), 0);
          return (
            <div key={empName} className="mb-3">
              <div className="d-flex align-items-center justify-content-between px-2 py-1 rounded mb-1"
                style={{ background: '#e8f4f8', borderRight: '4px solid #0d6efd' }}>
                <span className="fw-bold">
                  <i className="ri-user-line me-1 text-primary"></i>
                  {empName}
                  <Badge color="primary" className="ms-2">{empRows.length}</Badge>
                </span>
                <span className="text-primary fw-bold small">
                  צפוי: {fmt(empTotal)} ₪
                </span>
              </div>
              <div className="table-responsive">
                <Table bordered hover size="sm" className="text-nowrap mb-0">
                  {thead}
                  <tbody>
                    {empRows.map(r => <MissingRow key={r.id ?? r.terminalNumber} r={r} />)}
                  </tbody>
                  <tfoot>
                    <tr className="table-light fw-semibold">
                      <td colSpan={3} className="text-muted small">סה"כ {empRows.length}</td>
                      <td className="text-end">{fmt(empTotal / 1.18)}</td>
                      <td className="text-end">{fmt(empTotal)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </Table>
              </div>
            </div>
          );
        })}
        <div className="d-flex justify-content-end mt-2">
          <span className="badge bg-secondary fs-6">
            סה"כ כולל: {fmt(totalExpected)} ₪
          </span>
        </div>
      </>
    );
  };

  return (
    <React.Fragment>
      {/* סינון */}
      <Row className="mb-3 align-items-end">
        <Col xs="auto">
          <label className="form-label mb-1">{t('PROFITABILITY.COMMON.MONTH')}</label>
          <select className="form-select" value={month} onChange={e => setMonth(+e.target.value)}>
            {MONTHS_HE.slice(1).map((m, i) => (
              <option key={i + 1} value={i + 1}>{m}</option>
            ))}
          </select>
        </Col>
        <Col xs="auto">
          <label className="form-label mb-1">{t('PROFITABILITY.COMMON.YEAR')}</label>
          <select className="form-select" value={year} onChange={e => setYear(+e.target.value)}>
            {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </Col>
        <Col>
          <div className="text-muted text-end">
            {MONTHS_HE[month]} {year} —{' '}
            <span className="text-success fw-bold">{list.filter(r => r.received).length}</span> {t('PROFITABILITY.INVOICES_PAGE.SUMMARY').split('{{received}}')[1]?.split(',')[0] || 'התקבלו'},{' '}
            <span className="text-danger fw-bold">{missingList.length}</span> {t('PROFITABILITY.INVOICES_PAGE.SUMMARY').split('{{missing}}')[1] || 'חסרות'}
          </div>
        </Col>
      </Row>

      {/* טאבים */}
      <Nav tabs className="mb-3">
        <NavItem>
          <NavLink
            className={classnames({ active: activeTab === 'all' })}
            style={{ cursor: 'pointer' }}
            onClick={() => setActiveTab('all')}
          >
            {t('PROFITABILITY.INVOICES_PAGE.TAB_ALL')}
            <Badge color="primary" className="ms-2">{list.length}</Badge>
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={classnames({ active: activeTab === 'missing' })}
            style={{ cursor: 'pointer' }}
            onClick={() => setActiveTab('missing')}
          >
            {t('PROFITABILITY.INVOICES_PAGE.TAB_MISSING')}
            {missingList.length > 0 && (
              <Badge color="danger" className="ms-2">{missingList.length}</Badge>
            )}
          </NavLink>
        </NavItem>
      </Nav>

      <TabContent activeTab={activeTab}>
        <TabPane tabId="all">
          {renderTable(list, true)}
        </TabPane>
        <TabPane tabId="missing">
          {missingList.length === 0
            ? (
              <div className="text-center text-success py-4">
                <i className="ri-checkbox-circle-fill fs-1"></i>
                <p className="mt-2">{t('PROFITABILITY.INVOICES_PAGE.ALL_RECEIVED')}</p>
              </div>
            )
            : renderMissingTable(missingList)
          }
        </TabPane>
      </TabContent>

      {/* ── מודל תצוגה מקדימה ── */}
      <Modal isOpen={!!previewUrl} toggle={() => setPreviewUrl(null)}
        size="xl" centered scrollable>
        <ModalHeader toggle={() => setPreviewUrl(null)}
          className="border-bottom py-2">
          <i className="ri-file-text-line me-2 text-primary" />
          <span className="text-truncate" style={{ maxWidth: 500, display: 'inline-block' }}>
            {previewFileName || 'חשבונית'}
          </span>
          {previewUrl && previewUrl !== 'loading' && (
            <Button size="sm" color="link" className="ms-3 text-muted p-0"
              onClick={() => window.open(previewUrl, '_blank')}
              title="פתח בטאב חדש">
              <i className="ri-external-link-line" />
            </Button>
          )}
        </ModalHeader>
        <ModalBody style={{ height: '80vh', padding: 0, background: '#f5f5f5' }}>
          {previewUrl === 'loading' || previewLoading ? (
            <div className="d-flex align-items-center justify-content-center h-100">
              <div className="text-center text-muted">
                <i className="ri-loader-4-line fs-1 d-block mb-2" style={{ animation: 'spin 1s linear infinite' }} />
                טוען...
              </div>
            </div>
          ) : previewUrl && /\.(jpg|jpeg|png)$/i.test(previewFileName) ? (
            <div className="d-flex align-items-center justify-content-center h-100 p-3">
              <img src={previewUrl} alt="חשבונית"
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 4 }} />
            </div>
          ) : previewUrl ? (
            <embed src={previewUrl} type="application/pdf"
              style={{ width: '100%', height: '100%', border: 'none' }} />
          ) : null}
        </ModalBody>
      </Modal>

      {/* ── מודל אישור מחיקת חשבונית ── */}
      <Modal isOpen={!!deleteInvoiceId} toggle={() => setDeleteInvoiceId(null)} centered size="sm">
        <ModalHeader toggle={() => setDeleteInvoiceId(null)} className="border-bottom py-2">
          <i className="ri-delete-bin-line me-2 text-danger" />
          מחיקת חשבונית
        </ModalHeader>
        <ModalBody>
          <p className="mb-0">האם למחוק את החשבונית? פעולה זו אינה ניתנת לביטול.</p>
        </ModalBody>
        <ModalFooter className="py-2">
          <Button color="danger" size="sm" onClick={confirmDeleteInvoice}>
            <i className="ri-delete-bin-line me-1" />מחק
          </Button>
          <Button color="light" size="sm" onClick={() => setDeleteInvoiceId(null)}>ביטול</Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default InvoiceTracking;
